/**
 * useListPageSearch Hook
 *
 * A reusable hook for list pages that handles:
 * - Search with debounce (stable callbacks to prevent focus loss)
 * - URL param synchronization
 * - Pagination state
 * - Optional filter persistence via Zustand store (localStorage backed)
 *
 * The key feature is that all callbacks are stable and don't change when
 * URL params update, which prevents React Aria inputs from losing focus.
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useFilterStore } from '@/stores/filterStore'

/** Default debounce delay in milliseconds */
const DEFAULT_DEBOUNCE_MS = 300

interface UseListPageSearchOptions {
  /**
   * URL param key for the search query
   * @default 'q'
   */
  paramKey?: string

  /**
   * Debounce delay in milliseconds
   * @default 300
   */
  debounceMs?: number

  /**
   * Use JSON filter object format in URL (e.g., filter={"q":"search","status":"active"})
   * When false, uses simple param format (e.g., q=search)
   * @default false
   */
  useJsonFilter?: boolean

  /**
   * URL param key for the JSON filter object (only used when useJsonFilter is true)
   * @default 'filter'
   */
  filterKey?: string

  /**
   * Default items per page
   * @default 25
   */
  defaultPerPage?: number

  /**
   * Key for localStorage persistence. If provided, filters will be saved to localStorage
   * and restored when the page loads without URL filters.
   * Example: 'assistants', 'conversations'
   */
  persistKey?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface UseListPageSearchResult<TFilters extends Record<string, any>> {
  /** Current search query (local state, updates immediately on input) */
  searchQuery: string

  /** Stable handler for input onChange - won't cause focus loss */
  handleSearch: (query: string) => void

  /** Parsed filters from URL */
  filters: TFilters

  /** Stable handler to update filters directly */
  updateFilters: (newFilters: TFilters) => void

  /** Clear all filters (including persisted ones) */
  clearFilters: () => void

  /** Whether any filters are currently active */
  hasActiveFilters: boolean

  /** Current page number (from URL) */
  page: number

  /** Items per page (from URL) */
  perPage: number

  /** Stable handler for page change */
  handlePageChange: (newPage: number) => void

  /** Stable handler for page size change */
  handlePageSizeChange: (newPerPage: number) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useListPageSearch<TFilters extends Record<string, any> = Record<string, any>>(
  options: UseListPageSearchOptions = {}
): UseListPageSearchResult<TFilters> {
  const {
    paramKey = 'q',
    debounceMs = DEFAULT_DEBOUNCE_MS,
    useJsonFilter = false,
    filterKey = 'filter',
    defaultPerPage = 25,
    persistKey,
  } = options

  const [searchParams, setSearchParams] = useSearchParams()
  const isRestoringRef = useRef(false)

  // Use Zustand store for persistence
  const { getFilters, setFilters, clearFilters: clearStoredFilters } = useFilterStore()

  // Check if URL has filter params
  const hasUrlFilters = useJsonFilter
    ? searchParams.has(filterKey)
    : searchParams.has(paramKey)

  // Restore persisted filters to URL whenever we don't have URL filters but have persisted ones
  // This handles both initial mount and subsequent navigation via sidebar
  // The clearFilters function clears the store before changing URL,
  // so by the time this effect runs, the store will be empty if user clicked clear
  useEffect(() => {
    // Skip if no persistence key, already restoring, or URL has filters
    if (!persistKey || isRestoringRef.current || hasUrlFilters) {
      return
    }

    const persisted = getFilters(persistKey) as TFilters | null
    if (persisted && Object.keys(persisted).length > 0) {
      isRestoringRef.current = true
      setSearchParams((current) => {
        const params = new URLSearchParams(current)
        if (useJsonFilter) {
          params.set(filterKey, JSON.stringify(persisted))
        } else {
          const q = (persisted as Record<string, unknown>)[paramKey] as string | undefined
          if (q) {
            params.set(paramKey, q)
          }
        }
        return params
      }, { replace: true })
      // Reset flag after a tick to allow future restorations
      setTimeout(() => {
        isRestoringRef.current = false
      }, 0)
    }
  }, [persistKey, hasUrlFilters, useJsonFilter, filterKey, paramKey, setSearchParams, getFilters])

  // Parse filters from URL
  const filters = useMemo((): TFilters => {
    if (useJsonFilter) {
      const filterParam = searchParams.get(filterKey)
      if (filterParam) {
        try {
          return JSON.parse(filterParam) as TFilters
        } catch {
          return {} as TFilters
        }
      }
      return {} as TFilters
    }
    // Simple param mode - return search query in filters object
    const q = searchParams.get(paramKey)
    return (q ? { [paramKey]: q } : {}) as TFilters
  }, [searchParams, useJsonFilter, filterKey, paramKey])

  // Persist filters to store when they change
  useEffect(() => {
    if (persistKey) {
      // Always persist - if filters are empty, this will clear the store entry
      setFilters(persistKey, filters)
    }
  }, [persistKey, filters, setFilters])

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.keys(filters).length > 0
  }, [filters])

  // Get initial search value from URL or store
  const getInitialSearchValue = (): string => {
    // First check URL params
    if (useJsonFilter) {
      const urlValue = (filters as Record<string, unknown>).q as string
      if (urlValue) return urlValue
    } else {
      const urlValue = searchParams.get(paramKey)
      if (urlValue) return urlValue
    }

    // If no URL params and persistence is enabled, check store
    if (persistKey) {
      const persisted = getFilters(persistKey)
      if (persisted) {
        if (useJsonFilter) {
          return (persisted.q as string) || ''
        }
        return (persisted[paramKey] as string) || ''
      }
    }

    return ''
  }

  // Local search state (for immediate UI feedback while debouncing)
  const [searchQuery, setSearchQuery] = useState(getInitialSearchValue)

  // Sync search query with filters when they change (e.g., after URL restoration)
  useEffect(() => {
    if (useJsonFilter) {
      const filterQ = (filters as Record<string, unknown>).q as string
      if (filterQ !== undefined && filterQ !== searchQuery) {
        setSearchQuery(filterQ || '')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  // Refs for stable callbacks
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const filtersRef = useRef(filters)
  filtersRef.current = filters // Keep ref in sync without useEffect

  // Pagination from URL
  const page = parseInt(searchParams.get('page') || '1', 10)
  const perPage = parseInt(searchParams.get('perPage') || String(defaultPerPage), 10)

  // Stable filter update handler using functional setSearchParams
  const updateFilters = useCallback(
    (newFilters: TFilters) => {
      // Synchronously update the store before changing the URL to prevent
      // the restoration effect from restoring stale values
      if (persistKey) {
        const cleanNew = Object.fromEntries(
          Object.entries(newFilters).filter(([, v]) => v !== undefined && v !== null && v !== '')
        )
        if (Object.keys(cleanNew).length === 0) {
          clearStoredFilters(persistKey)
        } else {
          setFilters(persistKey, cleanNew)
        }
      }

      setSearchParams((current) => {
        const params = new URLSearchParams(current)
        if (useJsonFilter) {
          const cleanFilters = Object.fromEntries(
            Object.entries(newFilters).filter(([, v]) => v !== undefined && v !== null && v !== '')
          )
          if (Object.keys(cleanFilters).length > 0) {
            params.set(filterKey, JSON.stringify(cleanFilters))
          } else {
            params.delete(filterKey)
          }
        } else {
          const q = (newFilters as Record<string, unknown>)[paramKey] as string | undefined
          if (q) {
            params.set(paramKey, q)
          } else {
            params.delete(paramKey)
          }
        }
        params.set('page', '1') // Reset to first page on filter change
        return params
      })
    },
    [setSearchParams, useJsonFilter, filterKey, paramKey, persistKey, clearStoredFilters, setFilters]
  )

  // Stable debounced search handler
  const handleSearch = useCallback(
    (query: string) => {
      // Update local state immediately for responsive UI
      setSearchQuery(query)

      // If clearing search, immediately update the store to prevent restoration race condition
      // This runs before the debounced URL update to avoid the store restoring old values
      if (!query && persistKey) {
        if (useJsonFilter) {
          // Remove just the 'q' key from stored filters, keep other filters
          const currentStored = getFilters(persistKey) || {}
          const { q: _, ...rest } = currentStored
          if (Object.keys(rest).length > 0) {
            setFilters(persistKey, rest)
          } else {
            clearStoredFilters(persistKey)
          }
        } else {
          // Simple mode: search IS the filter, so clear entirely
          clearStoredFilters(persistKey)
        }
      }

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      // Debounce the URL update
      searchTimeoutRef.current = setTimeout(() => {
        if (useJsonFilter) {
          // Merge with existing filters using ref (avoids stale closure)
          updateFilters({ ...filtersRef.current, q: query || undefined } as TFilters)
        } else {
          updateFilters({ [paramKey]: query || undefined } as TFilters)
        }
      }, debounceMs)
    },
    [updateFilters, useJsonFilter, paramKey, debounceMs, persistKey, getFilters, setFilters, clearStoredFilters]
  )

  // Stable page change handler
  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((current) => {
        const params = new URLSearchParams(current)
        params.set('page', String(newPage))
        return params
      })
    },
    [setSearchParams]
  )

  // Stable page size change handler
  const handlePageSizeChange = useCallback(
    (newPerPage: number) => {
      setSearchParams((current) => {
        const params = new URLSearchParams(current)
        params.set('perPage', String(newPerPage))
        params.set('page', '1') // Reset to first page
        return params
      })
    },
    [setSearchParams]
  )

  // Clear all filters (including persisted ones)
  const clearFilters = useCallback(() => {
    // Clear store if persistence is enabled
    if (persistKey) {
      clearStoredFilters(persistKey)
    }

    // Clear search state
    setSearchQuery('')

    // Clear URL params
    setSearchParams((current) => {
      const params = new URLSearchParams(current)
      if (useJsonFilter) {
        params.delete(filterKey)
      } else {
        params.delete(paramKey)
      }
      params.set('page', '1')
      return params
    })
  }, [persistKey, useJsonFilter, filterKey, paramKey, setSearchParams, clearStoredFilters])

  return {
    searchQuery,
    handleSearch,
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    page,
    perPage,
    handlePageChange,
    handlePageSizeChange,
  }
}
